import * as React from 'react';
import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

import { BillingPlanEnum } from '@graphql';
import { useTranslation } from 'react-i18next';

type FeatureRow = {
  label: string;
  values: Record<Exclude<BillingPlanEnum, BillingPlanEnum.Freemium>, React.ReactNode>;
};

function CellValue({ value }: { value: React.ReactNode }) {
  if (value === 'yes') {
    return <CheckRoundedIcon fontSize="small" sx={{ color: '#00d050' }} />;
  }
  if (value === 'no') {
    return <CloseRoundedIcon fontSize="small" />;
  }
  return (
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  );
}

function PlanCard({
  title,
  price,
  highlighted,
  planKey,
  featureRows,
}: {
  title: string;
  price: string;
  highlighted?: boolean;
  planKey: Exclude<BillingPlanEnum, BillingPlanEnum.Freemium>;
  featureRows: FeatureRow[];
}) {
  const { t } = useTranslation('pricing');
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderWidth: 1,
        borderColor: highlighted ? `rgba(255, 255, 255, 0.5)` : `rgba(255, 255, 255, 0.12)`,
        borderStyle: 'outset',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {highlighted && (
        <Chip
          icon={<StarRoundedIcon />}
          label={t('tooltip.Popular')}
          color="success"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
          }}
        />
      )}

      <CardContent sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              {price}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {t('incentive.No commitment') + ' • ' + t('incentive.Per establishment')}
            </Typography>
          </Stack>

          <Divider />

          <Stack spacing={1.25}>
            {featureRows.map((r) => (
              <Stack
                key={`${planKey}-${r.label}`}
                direction="row"
                spacing={1.5}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color={r.values[planKey] !== 'no' ? 'text.primary' : 'text.secondary'}
                  sx={{ pr: 2, flex: 1 }}
                >
                  {r.label}
                </Typography>
                <Box
                  sx={{
                    minWidth: 36,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CellValue value={r.values[planKey]} />
                </Box>
              </Stack>
            ))}
          </Stack>

          <Stack spacing={2} divider={<Divider flexItem />}>
            <Button
              component={Link}
              fullWidth
              color={highlighted ? 'primary' : 'uncolored'}
              variant="contained"
              size="large"
              to={`/register/spot?plan=${planKey}`}
            >
              {t('cta.Choose plan X', { plan: title })}
            </Button>
            <Button
              component={Link}
              fullWidth
              variant="text"
              size="small"
              to="/privacy-policy"
              startIcon={<theme.icons.info />}
              sx={{ color: theme.palette.text.secondary }}
            >
              {t('cta.Learn more')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function HeroSpot() {
  const { t } = useTranslation('pricing');

  const plans: Array<{
    key: Exclude<BillingPlanEnum, BillingPlanEnum.Freemium>;
    title: string;
    price: string;
    highlighted?: boolean;
  }> = [
    { key: BillingPlanEnum.Essential, title: t('plans.Essential'), price: '49€ ht' },
    { key: BillingPlanEnum.Business, title: t('plans.Business'), price: '99€ ht' },
    { key: BillingPlanEnum.Premium, title: t('plans.Premium'), price: '129€ ht', highlighted: true },
  ];

  const featureRows: FeatureRow[] = [
    {
      label: t('feature.Support'),
      values: {
        [BillingPlanEnum.Essential]: t('support.FAQ'),
        [BillingPlanEnum.Business]: t('support.Standard'),
        [BillingPlanEnum.Premium]: t('support.Priority'),
      },
    },
    {
      label: t('feature.Statistics'),
      values: {
        [BillingPlanEnum.Essential]: 'yes',
        [BillingPlanEnum.Business]: 'yes',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
    {
      label: t('feature.Messaging'),
      values: {
        [BillingPlanEnum.Essential]: 'yes',
        [BillingPlanEnum.Business]: 'yes',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
    {
      label: t('feature.Account management (information, schedules, etc.)'),
      values: {
        [BillingPlanEnum.Essential]: 'yes',
        [BillingPlanEnum.Business]: 'yes',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
    {
      label: t('feature.Setup (payment, equipment, FAQ)'),
      values: {
        [BillingPlanEnum.Essential]: 'no',
        [BillingPlanEnum.Business]: 'yes',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
    {
      label: t('feature.Notifications'),
      values: {
        [BillingPlanEnum.Essential]: 'no',
        [BillingPlanEnum.Business]: 'yes',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
    {
      label: t('feature.Reservations & history'),
      values: {
        [BillingPlanEnum.Essential]: 'no',
        [BillingPlanEnum.Business]: 'yes',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
    {
      label: t('feature.Events'),
      values: {
        [BillingPlanEnum.Essential]: 'no',
        [BillingPlanEnum.Business]: 'no',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
    {
      label: t('feature.Admin access'),
      values: {
        [BillingPlanEnum.Essential]: 'no',
        [BillingPlanEnum.Business]: 'no',
        [BillingPlanEnum.Premium]: 'yes',
      },
    },
  ];

  return (
    <Container maxWidth="xl">
      <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
        <Grid item xs={12} md={5}>
          <Stack spacing={2.5}>
            <Stack spacing={3} direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1, opacity: 0.9 }}>
                {t('incentive.Pro Offers')}
              </Typography>
            </Stack>
            <Typography variant="h2" sx={{ fontWeight: 950, lineHeight: 1.05 }}>
              {t('incentive.Choose the plan that suits your business.')}
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {t(
                `incentive.Messaging and account management are included in the Essential plan. Upgrade to Business to activate reservations, or choose Premium for events, multi-site management, and roles & permissions.`
              )}
            </Typography>

            <Alert severity="success">
              {t(
                `incentive.With no cost per cover or booking commissions, benefit from a fixed and predictable monthly subscription.`
              )}
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ pt: 0.5 }}>
              <Button
                component={Link}
                variant="contained"
                size="large"
                to={`/register/spot?plan=${BillingPlanEnum.Premium}`}
                color="primary"
              >
                {t('cta.Create my premium account')}
              </Button>
              <Button component={Link} variant="outlined" color="uncolored" size="large" to="/support">
                {t('cta.Contact support')}
              </Button>
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip label={t('chip.Free registration')} variant="outlined" />
              <Chip label={t('chip.Quick trial')} variant="outlined" />
              <Chip label={t('chip.Included support')} variant="outlined" />
              <Chip label={t('chip.Transparent pricing')} variant="outlined" />
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} md={7} width="100%">
          <Grid container spacing={2} justifyContent="center">
            {plans.map((p) => (
              <Grid key={p.key} item size={{ xs: 12, sm: 4 }}>
                <PlanCard
                  title={p.title}
                  price={p.price}
                  highlighted={p.highlighted}
                  planKey={p.key}
                  featureRows={featureRows}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
